import { SET_PRODUCTS, SET_PRODUCTS_DATA_LOADING, SET_SEARCH_PRODUCTS, SET_SEARCH_PRODUCT_BY_ID, SET_FILTERED_PRODUCTS } from './types';
import store from '../../../../store';
import Axios from '../../../../common/util/Axios';

const FILTERED_PRODUCTS_API_BASE = process.env.REACT_APP_PRODUCTS_API_BASE_URL || 'http://localhost:5003';

export const createProduct = (product) => async (dispatch) => {
  try {
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: true });

    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;
    const requestBody = {
      ...product,
      companyId,
    };

    const queryParams = {
      companyId,
      branchId,
    };

    const res = await Axios.post(`/api/dashboard/products`, requestBody, {
      params: queryParams,
    });
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: false });
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * Add new product (full flow: type, variants, non-stock).
 * POST add-new-product; body: name, code, barcode, categoryId, dateAdded, isSerialCheck, type, isVariantAvailable, variantsDetails?, price?, cost?, isAvailable?.
 * Returns { success, data: { product, variants?, nonStock? } } or false on error.
 */
export const addNewProduct = (data) => async (dispatch) => {
  try {
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: true });
    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;
    const queryParams = { companyId, branchId };

    // Build FormData so files can be sent
    const formData = new FormData();
    // Append scalar fields
    formData.append('name', data.name ?? '');
    formData.append('code', data.code ?? '');
    formData.append('barcode', data.barcode ?? '');
    formData.append('categoryId', data.categoryId ?? '');
    formData.append('dateAdded', data.dateAdded ?? '');
    formData.append('isSerialCheck', String(data.isSerialCheck ?? false));
    formData.append('type', data.type ?? '');
    formData.append('isVariantAvailable', String(data.isVariantAvailable ?? false));

    // Append complex fields as JSON strings (parsed back in controller).
    // Strip images from variantsDetails — File objects can't be JSON-serialized.
    // Variant images are sent as separate named fields: variant_images_{index}.
    if (data.variantsDetails) {
      const variantsForJson = data.variantsDetails.map(({ images: _imgs, ...rest }) => rest);
      formData.append('variantsDetails', JSON.stringify(variantsForJson));

      data.variantsDetails.forEach((variant, idx) => {
        if (variant.images && variant.images.length > 0) {
          variant.images.forEach((img) => {
            formData.append(`variant_images_${idx}`, img.file, img.file.name);
          });
        }
      });
    }
    if (data.price != null) formData.append('price', data.price);
    if (data.cost != null) formData.append('cost', data.cost);
    if (data.isAvailable != null) formData.append('isAvailable', String(data.isAvailable));
    if (Array.isArray(data.modifierGroupIds)) {
      formData.append('modifierGroupIds', JSON.stringify(data.modifierGroupIds));
    }
    // Parent product images (only present when variantAvailability is false)
    if (data.images && data.images.length > 0) {
      data.images.forEach((img) => {
        formData.append('images', img.file, img.file.name);
      });
    }

    const res = await Axios.post(
      `/api/dashboard/products/add-new-product`,
      formData,
      {
        params: queryParams,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: false });
    return res.data?.data ?? res.data;
  } catch (err) {
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: false });
    return false;
  }
};

/**
 * Fetch filtered products with pagination (get-filtered-products API).
 * Dispatches SET_FILTERED_PRODUCTS with { data, totalRowCount }.
 */
export const getFilteredProducts = (keyword = '', page = 0, rowsPerPage = 10, filters = {}) => async (dispatch) => {
  const companyId = store.getState().authState.companyId;
  const branchId = store.getState().authState.branchId;

  const params = {
    keyword: String(keyword ?? '').trim(),
    noParentProduct: true,
    page: Number(page) || 0,
    rowsPerPage: Number(rowsPerPage) || 10,
    companyId,
    branchId,
    ...(filters?.category?.id ? { categoryId: filters.category.id } : {}),
    ...(filters?.supplier?.id ? { supplierId: filters.supplier.id } : {}),
    ...(filters?.productType && filters.productType !== 'ALL' ? { productType: filters.productType } : {}),
    ...(filters?.remainingQuantity && filters.remainingQuantity !== 'ALL' ? { remainingQuantity: filters.remainingQuantity } : {}),
    ...(filters?.lowStockThresholdValue ? { lowStockThresholdValue: filters.lowStockThresholdValue } : {}),
  };

  try {
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: true });
    const res = await Axios.get(`/api/dashboard/products/get-filtered-products`, { params });
    const payload = res.data?.data;
    if (!payload || !payload.data) {
      dispatch({ type: SET_FILTERED_PRODUCTS, payload: { data: [], totalRowCount: 0 } });
      return;
    }
    const totalRowCount = Array.isArray(payload.totalRowCount) && payload.totalRowCount[0]
      ? payload.totalRowCount[0].total_products
      : 0;
    dispatch({ type: SET_FILTERED_PRODUCTS, payload: { data: payload.data, totalRowCount } });
  } catch (err) {
    dispatch({ type: SET_FILTERED_PRODUCTS, payload: { data: [], totalRowCount: 0 } });
  }
};

export const getProducts = (params) => async (dispatch) => {
  try {
    dispatch({ type: SET_PRODUCTS_DATA_LOADING });

    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;
    const queryParams = {
      ...params,
      companyId,
      branchId,
    };

    const res = await Axios.get(`/api/dashboard/products/search-with-stock`, {
      params: queryParams,
    });

    dispatch({
      type: SET_PRODUCTS,
      payload: res.data.data,
    });
  } catch (err) {
    dispatch({
      type: SET_PRODUCTS,
      payload: [],
    });
  }
};

export const searchProducts = (keyword) => async (dispatch) => {
  try {
    dispatch({
      type: SET_SEARCH_PRODUCTS,
      payload: [],
    });

    const companyId = store.getState().authState.companyId;
    const res = await Axios.get(
      `/api/dashboard/products/search-by-code-name-barcode`,
      {
        params: { companyId, keyword },
      }
    );

    dispatch({
      type: SET_SEARCH_PRODUCTS,
      payload: res.data.data,
    });
  } catch (err) {
    dispatch({
      type: SET_SEARCH_PRODUCTS,
      payload: [],
    });
  }
};

export const searchProductById = (id) => async (dispatch) => {
  try {
    dispatch({
      type: SET_SEARCH_PRODUCT_BY_ID,
      payload: [],
    });

    const res = await Axios.get(`/api/dashboard/products/find-by-id`, {
      params: { id },
    });

    dispatch({
      type: SET_SEARCH_PRODUCT_BY_ID,
      payload: res.data.data,
    });

  } catch (err) {
    dispatch({
      type: SET_SEARCH_PRODUCT_BY_ID,
      payload: [],
    });
  }
};

export const fetchDefaultLowStockThreshold = () => async () => {
  try {
    const companyId = store.getState().authState.companyId;
    const res = await Axios.get(
      '/api/dashboard/settings/find-product-settings-for-branch',
      { params: { companyId } }
    );
    const settingsData = res.data?.data;
    if (Array.isArray(settingsData)) {
      const item = settingsData.find(
        (s) => s.param === 'default-low-stock-threshold'
      );
      if (item?.value) return String(item.value);
    }
    return null;
  } catch {
    return null;
  }
};

export const findProductByIdWithBranch = async (id, branchId) => {
  try {
    const resolvedBranchId = branchId ?? store.getState().authState.branchId;
    const res = await Axios.get(`/api/dashboard/products/find-by-id`, {
      params: { id, branchId: resolvedBranchId },
    });
    return res.data?.data ?? null;
  } catch (err) {
    return null;
  }
};

export const resetSearchedProduct = () => (dispatch) => {
  dispatch({
    type: SET_SEARCH_PRODUCT_BY_ID,
    payload: [],
  });
};

export const updateProduct = (product) => async (dispatch) => {
  try {
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: true });

    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;

    const { categoryname, ...updatedProduct } = product;

    const requestBody = {
      ...updatedProduct,
      companyId,
    };

    const queryParams = {
      companyId,
      branchId,
    };

    const res = await Axios.put(`/api/dashboard/products/${updatedProduct.id}`, requestBody, {
      params: queryParams,
    });

    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: false });
    return true;

  } catch (err) {
    return false;
  }
};

/**
 * Fetch single product for update form (parent only).
 * GET find-product-for-update?id=...
 */
export const findProductForUpdate = (id) => async () => {
  try {
    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;
    const res = await Axios.get(`/api/dashboard/products/find-product-for-update`, {
      params: { id, companyId, branchId },
    });
    return res.data?.data ?? res.data ?? null;
  } catch (err) {
    return null;
  }
};

/**
 * Fetch variant products for update form.
 * GET find-variants-for-update?parentProductId=...
 */
export const findVariantProductsForUpdate = (parentProductId) => async () => {
  try {
    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;
    const res = await Axios.get(`/api/dashboard/products/find-variants-for-update`, {
      params: { parentProductId, companyId, branchId },
    });
    const data = res.data?.data ?? res.data;
    return Array.isArray(data) ? data : [];
  } catch (err) {
    return [];
  }
};

/**
 * Update product with variants (single transaction).
 * POST update-product-with-variants with multipart/form-data so image files can be attached.
 */
export const updateProductWithVariants = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: true });
    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;

    const formData = new FormData();

    // Scalar fields
    formData.append('id', payload.id ?? '');
    formData.append('name', payload.name ?? '');
    formData.append('code', payload.code ?? '');
    formData.append('barcode', payload.barcode ?? '');
    formData.append('categoryid', payload.categoryid ?? '');
    formData.append('isSerialCheck', String(payload.isSerialCheck ?? false));
    formData.append('type', payload.type ?? '');
    formData.append('isVariantAvailable', String(payload.isVariantAvailable ?? false));

    // Optional non-stock fields
    if (payload.nonStockId != null) formData.append('nonStockId', payload.nonStockId);
    if (payload.price != null) formData.append('price', payload.price);
    if (payload.cost != null) formData.append('cost', payload.cost);
    if (payload.isAvailable != null) formData.append('isAvailable', String(payload.isAvailable));

    // Variants to delete
    if (payload.deleteVariants && payload.deleteVariants.length > 0) {
      formData.append('deleteVariants', JSON.stringify(payload.deleteVariants));
    }

    // Image IDs to delete (existing images the user removed)
    if (payload.deleteImageIds && payload.deleteImageIds.length > 0) {
      formData.append('deleteImageIds', JSON.stringify(payload.deleteImageIds));
    }

    // Variant details — strip images before JSON-serialising, send new files separately
    if (payload.variantsDetails && payload.variantsDetails.length > 0) {
      const variantsForJson = payload.variantsDetails.map(({ images: _imgs, ...rest }) => rest);
      formData.append('variantsDetails', JSON.stringify(variantsForJson));

      payload.variantsDetails.forEach((variant, idx) => {
        if (variant.images && variant.images.length > 0) {
          // Use variantId as the field key for existing variants so the backend
          // can match images to their correct variant even if the array is reordered.
          // Fall back to a positional key for brand-new variants that have no id yet.
          const imgKey = variant.variantId
            ? `variant_images_vid_${variant.variantId}`
            : `variant_images_new_${idx}`;
          variant.images.forEach((img) => {
            // Only upload new files; existing images (existingId) are kept server-side
            if (img.file) {
              formData.append(imgKey, img.file, img.file.name);
            }
          });
        }
      });
    }

    // Parent product images: only send new uploads (those with a file object)
    if (payload.images && payload.images.length > 0) {
      payload.images.forEach((img) => {
        if (img.file) {
          formData.append('images', img.file, img.file.name);
        }
      });
    }

    if (Array.isArray(payload.modifierGroupIds)) {
      formData.append('modifierGroupIds', JSON.stringify(payload.modifierGroupIds));
    }

    await Axios.post(`/api/dashboard/products/update-product-with-variants`, formData, {
      params: { companyId, branchId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: false });
    return true;
  } catch (err) {
    dispatch({ type: SET_PRODUCTS_DATA_LOADING, payload: false });
    return false;
  }
};
